import { clientSurface } from "./clientSurface";

type ProfileSession = { userId: number; activeContext: string | null } | null;
// The imperative API transport cannot read React context. The single AuthProvider
// initializes this session on sign-in/refresh and resets it on sign-out; revisions
// invalidate responses consumed outside React as well as mounted page requests.
let scopeKey = "anonymous";
let profileContext: string | null = null;
let revision = 0;

function normalizedProfileContext(activeContext: string | null | undefined): string {
  return activeContext?.startsWith("company:") ? activeContext : "personal";
}

/** One Personal scope includes retained worker/client presentations. A change
 * of account or company invalidates every pending API response. */
export function setApiProfile(user: ProfileSession): boolean {
  const context = normalizedProfileContext(user?.activeContext);
  const nextKey = user ? `${user.userId}:${context}` : "anonymous";
  if (nextKey === scopeKey) return false;
  scopeKey = nextKey;
  profileContext = user ? context : null;
  revision++;
  return true;
}

export function profileKey(user: { id: number; activeContext: string | null } | null): string {
  return user ? `${user.id}:${normalizedProfileContext(user.activeContext)}` : "anonymous";
}

/** Pin the entire asynchronous operation, including native preparation after body consumption.
 * A change away and back still invalidates the old request; comparing only current keys is insufficient. */
export function captureProfileGuard(): () => void {
  const startedRevision = revision;
  return () => {
    if (startedRevision !== revision) throw new DOMException("Profile changed during the request.", "AbortError");
  };
}

/** Pin uploads, downloads and JSON calls alike. Check body consumption too:
 * changing profiles while a response body is arriving must reject that data. */
export async function profileFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const assertCurrent = captureProfileGuard();
  const headers = new Headers(init.headers);
  headers.set("X-Client-Surface", clientSurface());
  if (profileContext) headers.set("X-Profile-Context", profileContext);
  const response = await fetch(input, { ...init, cache: "no-store", headers });
  assertCurrent();
  const readJson = response.json.bind(response);
  const readBlob = response.blob.bind(response);
  response.json = async () => { const body = await readJson(); assertCurrent(); return body; };
  response.blob = async () => { const body = await readBlob(); assertCurrent(); return body; };
  return response;
}
